"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { useLanguage } from '../../context/LanguageContext';
import {
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    InputAdornment,
    Alert,
    AlertTitle,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { fullScreenButtonStyle } from '../../utils/buttonStyles';
import theme from '../../theme';
import Toast from '../../components/utils/Toast';
import { useAuth } from '@/context/AuthContext'
import axios from 'axios';
import { UserEntityData, BirthData } from '@/models/entity/userEntity';

interface UserUpdateData {
    firstname: string;
    lastname: string;
    username?: string;
    email?: string;
    password?: string;
    gender?: string;
    birthdate?: string;
    street?: string;
    number?: string;
    complement?: string;
    zipCode?: string;
    city?: string;
    state?: string;
    country?: string;
    language?: string;
    profile?: {
        user_goal?: string;
        tarot_experience?: string;
        deck_preference?: string;
        birth_data?: BirthData;
        reading_frequency?: string;
        account?: string;
        ai_tone_preference?: string;
        credit_intro_ack?: boolean;
        privacy_consent?: boolean;
    };
}

export default function PersonalInformation() {
    const { t } = useTranslation();
    const { user, refreshUserData } = useAuth()
    const { currentLanguage, changeLanguage } = useLanguage();

    // Get user data from the UserEntity object
    const userData: UserEntityData = user?.toJSON() || {
        _id: '',
        firstname: '',
        lastname: '',
        username: '',
        email: '',
        password: '',
        birthdate: null,
        credits: 0,
        lastUpdated: null,
        picture: '',
        gender: '',
        street: '',
        number: '',
        complement: '',
        zipCode: '',
        city: '',
        state: '',
        country: '',
        profile: {},
        onboarding: false
    }



    // Local state for form fields to avoid validation errors during typing
    const [formData, setFormData] = useState({
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        username: userData.username || '',
        email: userData.email || '',
        gender: userData.gender || '',
        birthdate: userData.birthdate,
        street: userData.street || '',
        number: userData.number || '',
        complement: userData.complement || '',
        zipCode: userData.zipCode || '',
        city: userData.city || '',
        state: userData.state || '',
        country: userData.country || '',
        language: userData.language || userData.profile?.language || currentLanguage || ''
    });

    // Update form data when user ID changes (meaning a different user logged in) or language changes
    useEffect(() => {
        if (user) {
            const freshData = user.toJSON();
            setFormData({
                firstname: freshData.firstname || '',
                lastname: freshData.lastname || '',
                username: freshData.username || '',
                email: freshData.email || '',
                gender: freshData.gender || '',
                birthdate: freshData.birthdate,
                street: freshData.street || '',
                number: freshData.number || '',
                complement: freshData.complement || '',
                zipCode: freshData.zipCode || '',
                city: freshData.city || '',
                state: freshData.state || '',
                country: freshData.country || '',
                language: freshData.language || freshData.profile?.language || currentLanguage || ''
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id, currentLanguage]);

    // Only keep state for password fields and UI state
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Helper function to get formatted date for input
    const getFormattedDate = () => {
        if (formData.birthdate) {
            const date = new Date(formData.birthdate);
            if (!isNaN(date.getTime())) {
                return date.toISOString().split('T')[0];
            }
        }
        return "";
    }

    // Helper function to get user language
    const getUserLanguage = () => {
        const userLanguage = formData.language;
        if (userLanguage) {
            // If it's already in the correct format (en-US, pt-BR), use it directly
            if (userLanguage.includes('-')) {
                return userLanguage;
            }
            // If it's a language name, convert it to language code (for backward compatibility)
            const languageNameToCodeMap: Record<string, string> = {
                'English': 'en-US',
                'Portuguese': 'pt-BR',
                'Spanish': 'es-ES',
                'French': 'fr-FR',
                'German': 'de-DE'
            };
            return languageNameToCodeMap[userLanguage] || userLanguage;
        }
        // Fallback to current language context
        return currentLanguage || '';
    }

    // Function to update form field
    const updateFormField = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Validate required fields
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.firstname || formData.firstname.trim().length === 0) {
            errors.firstname = t('personalInfo.validation.firstnameRequired') || 'First name is required';
        }

        if (!formData.lastname || formData.lastname.trim().length === 0) {
            errors.lastname = t('personalInfo.validation.lastnameRequired') || 'Last name is required';
        }

        if (!formData.email || formData.email.trim().length === 0) {
            errors.email = t('personalInfo.validation.emailRequired') || 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = t('personalInfo.validation.emailInvalid') || 'Please enter a valid email address';
        }

        if (password && password.length > 0 && password.length < 8) {
            errors.password = t('personalInfo.validation.passwordMinLength') || 'Password must be at least 8 characters';
        }

        if (password && password.length > 0 && password !== confirmPassword) {
            errors.confirmPassword = t('personalInfo.validation.passwordsDoNotMatch') || 'Passwords do not match';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Validate individual field on blur
    const validateField = (field: string, value: string) => {
        const errors: Record<string, string> = { ...validationErrors };

        switch (field) {
            case 'firstname':
                if (!value || value.trim().length === 0) {
                    errors.firstname = t('personalInfo.validation.firstnameRequired') || 'First name is required';
                } else {
                    delete errors.firstname;
                }
                break;
            case 'lastname':
                if (!value || value.trim().length === 0) {
                    errors.lastname = t('personalInfo.validation.lastnameRequired') || 'Last name is required';
                } else {
                    delete errors.lastname;
                }
                break;
            case 'email':
                if (!value || value.trim().length === 0) {
                    errors.email = t('personalInfo.validation.emailRequired') || 'Email is required';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    errors.email = t('personalInfo.validation.emailInvalid') || 'Please enter a valid email address';
                } else {
                    delete errors.email;
                }
                break;
            case 'password':
                if (value && value.length > 0 && value.length < 8) {
                    errors.password = t('personalInfo.validation.passwordMinLength') || 'Password must be at least 8 characters';
                } else {
                    delete errors.password;
                }
                // Re-validate confirmPassword if password changes
                if (confirmPassword && value !== confirmPassword) {
                    errors.confirmPassword = t('personalInfo.validation.passwordsDoNotMatch') || 'Passwords do not match';
                } else {
                    delete errors.confirmPassword;
                }
                break;
            case 'confirmPassword':
                if (password && password.length > 0 && value !== password) {
                    errors.confirmPassword = t('personalInfo.validation.passwordsDoNotMatch') || 'Passwords do not match';
                } else {
                    delete errors.confirmPassword;
                }
                break;
        }

        setValidationErrors(errors);
    };

    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

    // Centralized input field styling
    const inputFieldStyle = {
        '& .MuiInputBase-root': {
            padding: '5px',

        },
        '& .MuiInputBase-input': {
            padding: '12px 14px',
        },
        '& .MuiOutlinedInput-root': {
            padding: '0px',

            '& fieldset': {
                borderRadius: '8px',
            },
            '&:hover': {

            },
            '&.Mui-focused': {

            },
        },
    };

    // Centralized select field styling
    const selectFieldStyle = {
        '& .MuiInputBase-root': {
            padding: '5px',
        },
        '& .MuiSelect-select': {
            padding: '12px 14px',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderRadius: '8px',
            },
            '&:hover': {
            },
            '&.Mui-focused': {

            },
        },
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form before submitting to backend
        const isValid = validateForm();

        if (!isValid) {
            // Use setTimeout to ensure validation errors state has been updated
            setTimeout(() => {
                // Count how many errors there are
                const errorFields = Object.keys(validationErrors);
                const errorCount = errorFields.length;
                const errorMessage = errorCount === 1
                    ? t('personalInfo.validation.pleaseFixError') || 'Please fix the error in the form before submitting'
                    : `${t('personalInfo.validation.pleaseFixErrors') || 'Please fix the errors in the form before submitting'} (${errorCount} ${t('personalInfo.validation.errors') || 'errors'})`;

                setToastMessage(errorMessage);
                setToastSeverity('error');
                setToastOpen(true);

                // Scroll to the first error field
                if (errorFields.length > 0) {
                    const firstErrorField = errorFields[0];
                    const errorElement = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
                    if (errorElement) {
                        errorElement.focus();
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, 100);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error(t('personalInfo.messages.noAuthToken'));
            }

            const updateData: UserUpdateData = {
                firstname: formData.firstname.trim(),
                lastname: formData.lastname.trim(),
                username: formData.username,
                email: formData.email.trim(),
                gender: formData.gender,
                birthdate: getFormattedDate(),
                street: formData.street,
                number: formData.number,
                complement: formData.complement,
                zipCode: formData.zipCode,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                language: getUserLanguage(), // Store language directly as language code
                profile: {
                    ...(user?.profile || {}), // Preserve existing profile data
                }
            };

            // Only include password if it's not empty
            if (password) {
                updateData.password = password;
            }

            // Remove fields that are truly undefined or null (but keep empty strings)
            Object.keys(updateData).forEach(key => {
                const value = updateData[key as keyof UserUpdateData];
                if (value === undefined || value === null) {
                    delete updateData[key as keyof UserUpdateData];
                }
            });

            await axios.put(
                `${import.meta.env.VITE_API_URL}/api/user/profile`,
                updateData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Refresh user data from the server to update the UI
            await refreshUserData();

            // Clear sensitive fields
            setPassword('');
            setConfirmPassword('');
            setValidationErrors({});

            setToastMessage(t('personalInfo.messages.profileSavedSuccess'));
            setToastSeverity('success');
            setToastOpen(true);

        } catch (error) {
            console.error('Error updating profile:', error);
            setToastMessage(t('personalInfo.messages.failedToUpdateProfile'));
            setToastSeverity('error');
            setToastOpen(true);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ p: 3, pt: 4, pb: 12, maxHeight: 'calc(100vh - 120px)', maxWidth: 600, width: '100%', mx: 'auto', overflow: 'auto', boxSizing: 'border-box' }}>
                <Typography sx={{ mb: 3, textAlign: 'center' }}>
                    {t('personalInfo.description')}
                </Typography>

                <Alert
                    severity="info"
                    sx={{
                        display: 'none',
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: 'rgba(43, 46, 74, 0.05)',
                        border: '1px solid rgba(43, 46, 74, 0.15)',
                        '& .MuiAlert-icon': {
                            color: 'primary.main'
                        }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {t('personalInfo.requiredFieldsTitle') || 'Required Fields'}
                    </AlertTitle>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {t('personalInfo.requiredFieldsMessage') || 'Fields marked with an asterisk (*) are required and cannot be left empty.'}
                    </Typography>
                </Alert>

                <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr' }}>
                            <TextField
                                fullWidth
                                required
                                name="firstname"
                                label={t('personalInfo.firstName')}
                                value={formData.firstname}
                                onChange={(e) => updateFormField('firstname', e.target.value)}
                                onBlur={(e) => validateField('firstname', e.target.value)}
                                error={!!validationErrors.firstname}
                                helperText={validationErrors.firstname}
                                sx={inputFieldStyle}
                                InputLabelProps={{
                                    sx: {
                                        '& .MuiFormLabel-asterisk': {
                                            color: 'error.main'
                                        }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                required
                                name="lastname"
                                label={t('personalInfo.lastName')}
                                value={formData.lastname}
                                onChange={(e) => updateFormField('lastname', e.target.value)}
                                onBlur={(e) => validateField('lastname', e.target.value)}
                                error={!!validationErrors.lastname}
                                helperText={validationErrors.lastname}
                                sx={inputFieldStyle}
                                InputLabelProps={{
                                    sx: {
                                        '& .MuiFormLabel-asterisk': {
                                            color: 'error.main'
                                        }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                label={t('personalInfo.username')}
                                value={formData.username}
                                onChange={(e) => updateFormField('username', e.target.value)}
                                sx={inputFieldStyle}
                            />

                            <TextField
                                fullWidth
                                required
                                name="email"
                                label={t('personalInfo.email')}
                                type="email"
                                value={formData.email}
                                onChange={(e) => updateFormField('email', e.target.value)}
                                onBlur={(e) => validateField('email', e.target.value)}
                                error={!!validationErrors.email}
                                helperText={validationErrors.email}
                                sx={inputFieldStyle}
                                InputLabelProps={{
                                    sx: {
                                        '& .MuiFormLabel-asterisk': {
                                            color: 'error.main'
                                        }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                name="password"
                                label={t('personalInfo.password')}
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (validationErrors.password) {
                                        setValidationErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.password;
                                            return newErrors;
                                        });
                                    }
                                }}
                                onBlur={(e) => validateField('password', e.target.value)}
                                autoComplete="new-password"
                                error={!!validationErrors.password}
                                helperText={validationErrors.password || t('personalInfo.passwordHelper')}
                                sx={inputFieldStyle}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                sx={{ marginRight: '10px' }}
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                fullWidth
                                name="confirmPassword"
                                label={t('personalInfo.confirmPassword')}
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    if (validationErrors.confirmPassword) {
                                        setValidationErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.confirmPassword;
                                            return newErrors;
                                        });
                                    }
                                }}
                                onBlur={(e) => validateField('confirmPassword', e.target.value)}
                                autoComplete="new-password"
                                error={!!validationErrors.confirmPassword}
                                helperText={validationErrors.confirmPassword}
                                sx={inputFieldStyle}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                sx={{ marginRight: '10px' }}
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl sx={{ flex: 1, ...selectFieldStyle }}>
                                    <InputLabel>{t('personalInfo.gender')}</InputLabel>
                                    <Select
                                        value={formData.gender}
                                        label={t('personalInfo.gender')}
                                        onChange={(e) => updateFormField('gender', e.target.value)}
                                    >
                                        <MenuItem value="">{t('personalInfo.selectGender')}</MenuItem>
                                        <MenuItem value="male">{t('personalInfo.male')}</MenuItem>
                                        <MenuItem value="female">{t('personalInfo.female')}</MenuItem>
                                        <MenuItem value="non-binary">{t('personalInfo.other')}</MenuItem>
                                        <MenuItem value="not-specified">{t('personalInfo.preferNotToSay')}</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    sx={{ flex: 1, ...inputFieldStyle }}
                                    label={t('personalInfo.dateOfBirth')}
                                    type="date"
                                    value={getFormattedDate()}
                                    onChange={(e) => updateFormField('birthdate', new Date(e.target.value))}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Box>

                            <FormControl fullWidth sx={selectFieldStyle}>
                                <InputLabel>{t('personalInfo.preferredLanguage')}</InputLabel>
                                <Select
                                    key={getUserLanguage()} // Force re-render when language changes
                                    value={getUserLanguage()}
                                    label={t('personalInfo.preferredLanguage')}
                                    onChange={async (e) => {
                                        const newLanguageCode = e.target.value;
                                        console.log('Language changed to:', newLanguageCode);
                                        if (newLanguageCode) {
                                            try {
                                                // First update the language context
                                                await changeLanguage(newLanguageCode);

                                                // Update form field
                                                updateFormField('language', newLanguageCode);

                                                // Then update the backend with language code directly
                                                const token = localStorage.getItem('token');
                                                if (token) {
                                                    await axios.put(
                                                        `${import.meta.env.VITE_API_URL}/api/user/profile`,
                                                        {
                                                            language: newLanguageCode // Store language code directly
                                                        },
                                                        {
                                                            headers: {
                                                                Authorization: `Bearer ${token}`,
                                                                'Content-Type': 'application/json'
                                                            }
                                                        }
                                                    );

                                                    // Refresh user data to update the context
                                                    await refreshUserData();
                                                }
                                            } catch (error) {
                                                console.error('Error updating language:', error);
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="">{t('personalInfo.selectPreferredLanguage')}</MenuItem>
                                    <MenuItem value="en-US">{t('profileOptions.languages.english')}</MenuItem>
                                    <MenuItem value="pt-BR">{t('profileOptions.languages.portuguese')}</MenuItem>
                                    <MenuItem value="es-ES">{t('profileOptions.languages.spanish')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4, display: "none" }}>
                        <Typography variant="h5" sx={{ mb: 3 }}>{t('personalInfo.address')}</Typography>
                        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(2, 1fr)' }}>
                            <TextField
                                fullWidth
                                label={t('personalInfo.street')}
                                value={formData.street}
                                onChange={(e) => updateFormField('street', e.target.value)}
                                sx={inputFieldStyle}
                            />

                            <TextField
                                fullWidth
                                label={t('personalInfo.number')}
                                value={formData.number}
                                onChange={(e) => updateFormField('number', e.target.value)}
                                sx={inputFieldStyle}
                            />

                            <TextField
                                fullWidth
                                label={t('personalInfo.complement')}
                                value={formData.complement}
                                onChange={(e) => updateFormField('complement', e.target.value)}
                                sx={inputFieldStyle}
                            />

                            <TextField
                                fullWidth
                                label={t('personalInfo.zipCode')}
                                value={formData.zipCode}
                                onChange={(e) => updateFormField('zipCode', e.target.value)}
                                sx={inputFieldStyle}
                            />

                            <TextField
                                fullWidth
                                label={t('personalInfo.city')}
                                value={formData.city}
                                onChange={(e) => updateFormField('city', e.target.value)}
                                sx={inputFieldStyle}
                            />

                            <TextField
                                fullWidth
                                label={t('personalInfo.state')}
                                value={formData.state}
                                onChange={(e) => updateFormField('state', e.target.value)}
                                sx={inputFieldStyle}
                            />

                            <TextField
                                fullWidth
                                label={t('personalInfo.country')}
                                value={formData.country}
                                onChange={(e) => updateFormField('country', e.target.value)}
                                sx={inputFieldStyle}
                            />
                        </Box>
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        onClick={handleSubmit}
                        fullWidth
                        sx={fullScreenButtonStyle}
                    >
                        {t('settings.saveChanges')}
                    </Button>

                </form>

                <Toast
                    open={toastOpen}
                    message={toastMessage}
                    severity={toastSeverity}
                    onClose={() => setToastOpen(false)}
                />
            </Box>
        </ThemeProvider>
    )
}
